<template>
  <div>
    <page-title-bar :customTitle="customTitle"></page-title-bar>
    <v-container fluid grid-list-xl pt-0>
      <v-layout row wrap justify-center>
        <v-flex xs12>
          <v-toolbar :elevation="0" height="auto">
            <v-layout row justify-space-between align-center style="padding: 32px">
              <!-- Month Picker -->
              <v-flex xs12 md3>
                <div style="max-width: 290px">
                  <v-menu :close-on-content-click="false" v-model="menu_month_picker" :nudge-right="40" lazy
                    transition="scale-transition" offset-y full-width min-width="290px" max-width="290px"
                    :disabled="loader">
                    <v-text-field slot="activator" v-model="chosen_month" label="Chọn tháng" :disabled="loader"
                      prepend-icon="event" readonly hide-details></v-text-field>
                    <v-date-picker min="2025-03" :max="maxMonth" locale="vi-VI" type="month" v-model="chosen_month"
                      @input="menu_month_picker = false"></v-date-picker>
                  </v-menu>
                </div>
              </v-flex>

              <!-- Status Info -->
              <v-flex xs12 md6>
                <v-layout row wrap align-center justify-center>
                  <v-flex shrink>
                    <div class="text-center px-2">
                      <div class="caption grey--text text--darken-1 mb-1">Trạng thái tháng được chọn</div>
                      <v-chip 
                        v-if="isLocked" 
                        color="green" 
                        text-color="white" 
                        small 
                        label
                        class="font-weight-medium"
                      >
                        <v-icon left x-small>check_circle</v-icon>
                        Đã chốt
                      </v-chip>
                      <v-chip 
                        v-else 
                        color="orange" 
                        text-color="white" 
                        small 
                        label
                        class="font-weight-medium"
                      >
                        <v-icon left x-small>schedule</v-icon>
                        Chưa chốt
                      </v-chip>
                    </div>
                  </v-flex>
                  <v-flex shrink>
                    <div class="text-center px-2">
                      <div class="caption grey--text text--darken-1 mb-1">Tháng gần nhất đã chốt</div>
                      <div v-if="latestLockedMonth" class="body-2 font-weight-medium primary--text">
                        {{ formatMonthDisplay(latestLockedMonth) }}
                      </div>
                      <div v-else class="body-2 grey--text">
                        Chưa có
                      </div>
                    </div>
                  </v-flex>
                </v-layout>
              </v-flex>

              <!-- Action Button -->
              <v-flex xs12 md3 class="text-right">
                <v-btn color="success" class="mr-2" @click="exportExcel" :loading="exporting" :disabled="loader || !reportData">
                  <v-icon left small>cloud_download</v-icon>
                  Xuất Excel
                </v-btn>

                <v-btn v-if="!isLocked" color="primary" :loading="locking" :disabled="loader || !!errorMessage"
                  @click="confirmLockReport">
                  <v-icon left small>lock</v-icon>
                  CHỐT BÁO CÁO
                </v-btn>
                <v-btn v-else color="grey" disabled flat>
                  <v-icon left small>lock</v-icon>
                  ĐÃ CHỐT
                </v-btn>
              </v-flex>
            </v-layout>
          </v-toolbar>

          <div class="mt-3">
            <v-card>
              <v-card-title class="headline">
                Báo cáo lợi nhuận chưa phân phối (Tháng {{ formatMonth(chosen_month) }})
              </v-card-title>
              <v-divider></v-divider>

              <!-- Loading State -->
              <v-card-text v-if="loader" class="text-center pa-5">
                <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
                <p class="mt-3">Đang tải báo cáo...</p>
              </v-card-text>

              <!-- Report Content -->
              <v-card-text v-else-if="reportData">
                <div class="table-responsive">
                  <table class="report-table">
                    <thead>
                      <tr>
                        <th class="text-left" style="min-width: 300px">Chỉ tiêu</th>
                        <th class="text-right" style="min-width: 150px">Số tiền</th>
                      </tr>
                    </thead>
                    <tbody>
                      <!-- OPENING -->
                      <tr>
                        <td><strong>Số dư đầu kỳ</strong></td>
                        <td class="text-right"><strong>{{ formatCurrency(reportData.opening_balance) }}</strong></td>
                      </tr>

                      <!-- PROFIT -->
                      <tr class="highlight-row">
                        <td>1. Lợi nhuận trong kỳ</td>
                        <td class="text-right">{{ formatCurrency(reportData.profit_for_period) }}</td>
                      </tr>

                      <!-- LOAN -->
                      <tr class="highlight-row" @click="toggleExpand('loan')" style="cursor: pointer">
                        <td>
                          2. Trả nợ vay
                          <v-icon small class="ml-1">
                             {{ expanded.loan ? 'expand_less' : 'expand_more' }}
                          </v-icon>
                        </td>
                        <td class="text-right">
                          <strong>{{ formatCurrency(reportData.loan_repayment_net) }}</strong>
                        </td>
                      </tr>
                      
                      <!-- Loan Details -->
                      <template v-if="expanded.loan">
                         <tr v-for="(item, idx) in sortedLoanDetails" :key="'loan_'+idx" class="detail-row">
                           <td class="pl-4">- {{ item.text }}</td>
                           <td class="text-right">{{ formatCurrency(item.amount) }}</td>
                         </tr>
                      </template>

                      <!-- PERSONAL SPENDING -->
                      <tr class="highlight-row" @click="toggleExpand('personal')" style="cursor: pointer">
                        <td>
                          3. Chi cá nhân (Thu/Chi LNCCP)
                          <v-icon small class="ml-1">
                             {{ expanded.personal ? 'expand_less' : 'expand_more' }}
                          </v-icon>
                        </td>
                        <td class="text-right">
                          <strong>{{ formatCurrency(reportData.personal_spending_net) }}</strong>
                        </td>
                      </tr>

                      <!-- Personal Details -->
                      <template v-if="expanded.personal">
                         <tr v-for="(item, idx) in sortedPersonalDetails" :key="'personal_'+idx" class="detail-row">
                           <td class="pl-4">- {{ item.text }}</td>
                           <td class="text-right">{{ formatCurrency(item.amount) }}</td>
                         </tr>
                      </template>

                      <!-- CLOSING -->
                      <tr class="total-row">
                        <td><strong>Số dư cuối kỳ</strong></td>
                        <td class="text-right"><strong>{{ formatCurrency(reportData.closing_balance) }}</strong></td>
                      </tr>

                    </tbody>
                  </table>
                </div>
              </v-card-text>

              <!-- Error State -->
              <v-card-text v-else-if="errorMessage" class="text-center pa-5">
                <v-icon size="60" color="orange">warning</v-icon>
                <p class="mt-3 orange--text">{{ errorMessage }}</p>
              </v-card-text>

              <v-card-text v-else class="text-center pa-5">
                <v-icon size="60" color="grey lighten-1">error_outline</v-icon>
                <p class="mt-3 grey--text">Không có dữ liệu</p>
              </v-card-text>
            </v-card>
          </div>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import api from "../../api";
import moment from "moment";
import swal from "sweetalert2";
import { ExcelExporter } from '../../helpers/ExcelExporter';

export default {
  data() {
    return {
      loader: false,
      locking: false,
      exporting: false,
      reportData: null,
      isLocked: false,
      lockedAt: null,
      lockedBy: null,
      latestLockedMonth: null,
      errorMessage: null,
      menu_month_picker: false,
      chosen_month: moment().format("YYYY-MM"),
      expanded: {
        loan: true,
        personal: true,
      }
    };
  },
  computed: {
    customTitle() {
      return "Báo cáo LNCPP theo tháng";
    },
    maxMonth() {
      return moment().add(1, 'month').format("YYYY-MM");
    },
    sortedLoanDetails() {
      if (!this.reportData || !this.reportData.loan_details) return [];
      return [...this.reportData.loan_details].sort((a, b) => a.text.localeCompare(b.text));
    },
    sortedPersonalDetails() {
       if (!this.reportData || !this.reportData.personal_spending_details) return [];
       return [...this.reportData.personal_spending_details].sort((a, b) => a.text.localeCompare(b.text));
    }
  },
  watch: {
    chosen_month: function() {
      this.getData();
    }
  },
  mounted() {
    this.getData();
    this.fetchLockedMonths();
  },
  methods: {
    formatMonth(month) {
      return moment(month).format("MM/YYYY");
    },
    formatMonthDisplay(month) {
      if (!month) return "";
      return moment(month).format("MM/YYYY");
    },
    formatDateTime(date) {
      if (!date) return "";
      return moment(date).format("HH:mm DD/MM/YYYY");
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return "-";
      const num = Number(value);
      if (num === 0) return "0";
      const formatted = Math.abs(num).toLocaleString("vi-VN");
      // Negative in parenthesis
      return num < 0 ? `(${formatted})` : formatted;
    },
    toggleExpand(key) {
      this.expanded[key] = !this.expanded[key];
    },
    async fetchLockedMonths() {
        try {
            const result = await api.getLockedUndistributedProfitMonths();
            if (result.status === 200 && result.data && result.data.locked_months && result.data.locked_months.length > 0) {
                // Sorted desc by default from API?
                this.latestLockedMonth = result.data.locked_months[0].month;
            } else {
                this.latestLockedMonth = null;
            }
        } catch (error) {
            console.error(error);
        }
    },
    async getData() {
      this.loader = true;
      this.reportData = null;
      this.errorMessage = null;

      try {
        const result = await api.getMonthlyUndistributedProfitReport(`month=${this.chosen_month}`);
        if (result.status === 200 || result.status === 304) { // 304?
           // Actually api wrapper returns { data, status } but if success implies 200 usually.
           const data = result.data;
           this.isLocked = data.is_locked;
           this.lockedAt = data.locked_at;
           this.lockedBy = data.locked_by;
           this.reportData = data.report_data;
        } else {
           this.errorMessage = result.message || "Lỗi tải báo cáo";
        }
      } catch (error) {
        console.error(error);
        this.errorMessage = (error.response && error.response.data && error.response.data.message) || error.message;
      }
      this.loader = false;
    },
    async confirmLockReport() {
      const result = await swal.fire({
        title: 'Chốt báo cáo?',
        text: `Bạn có chắc muốn chốt báo cáo tháng ${this.formatMonth(this.chosen_month)}? Thao tác này không thể hoàn tác.`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Đồng ý',
        cancelButtonText: 'Hủy'
      });

      if (result.value) {
        this.lockReport();
      }
    },
    async lockReport() {
      this.locking = true;
      try {
        const result = await api.lockUndistributedProfitReport({ month: this.chosen_month });
        if (result.status === 200) {
          swal.fire("Thành công", "Đã chốt báo cáo", "success");
          this.getData(); // Reload
          this.fetchLockedMonths();
        } else {
          swal.fire("Lỗi", result.message || "Không thể chốt báo cáo", "error");
        }
      } catch (error) {
        swal.fire("Lỗi", (error.response && error.response.data && error.response.data.message) || "Lỗi server", "error");
      }
      this.locking = false;
    },
    async exportExcel() {
        if (!this.reportData) return;
        this.exporting = true;
        try {
            const data = [];
            
            // 1. Opening
            data.push({ 
                name: 'Số dư đầu kỳ', 
                amount: this.reportData.opening_balance, 
                style: { bold: true } 
            });

            // 2. Profit
            data.push({ 
                name: '1. Lợi nhuận trong kỳ', 
                amount: this.reportData.profit_for_period,
                style: { background: 'FFE3F2FD' } // Light Blue
            });

            // 3. Loan
            data.push({ 
                name: '2. Trả nợ vay', 
                amount: this.reportData.loan_repayment_net, 
                style: { bold: true, background: 'FFE3F2FD' } 
            });
            if (this.sortedLoanDetails) {
                this.sortedLoanDetails.forEach(item => {
                    data.push({
                        name: item.text,
                        amount: item.amount,
                        style: { indent: 1 },
                        outlineLevel: 1
                    });
                });
            }

            // 4. Personal
            data.push({ 
                name: '3. Chi cá nhân (Thu/Chi LNCCP)', 
                amount: this.reportData.personal_spending_net, 
                style: { bold: true, background: 'FFE3F2FD' } 
            });
            if (this.sortedPersonalDetails) {
                this.sortedPersonalDetails.forEach(item => {
                    data.push({
                        name: item.text,
                        amount: item.amount,
                        style: { indent: 1 },
                        outlineLevel: 1
                    });
                });
            }

            // 5. Closing
             data.push({ 
                name: 'Số dư cuối kỳ', 
                amount: this.reportData.closing_balance, 
                style: { bold: true, background: 'FFFFF3E0' } // Light Orange
            });

            await ExcelExporter.exportReport({
                title: 'BÁO CÁO LỢI NHUẬN CHƯA PHÂN PHỐI',
                subtitle: `Tháng ${this.formatMonth(this.chosen_month)}`,
                columns: [
                    { header: 'Chỉ tiêu', key: 'name', width: 40 },
                    { header: 'Số tiền', key: 'amount', width: 20, align: 'right', format: 'currency' }
                ],
                data: data,
                fileName: `BaoCaoLNCPP_${this.chosen_month}`
            });

        } catch (error) {
            console.error(error);
            swal.fire("Lỗi", "Không thể xuất file Excel", "error");
        }
        this.exporting = false;
    }
  }
};
</script>

<style scoped>
.table-responsive {
  overflow-x: auto;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
}

.report-table th,
.report-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
}

.report-table th {
  background-color: #f5f5f5;
  font-weight: 600;
}

.report-table .highlight-row td {
  background-color: #e3f2fd;
  font-weight: normal;
}
.report-table .highlight-row:hover td {
   background-color: #bbdefb;
}

.report-table .total-row td {
  background-color: #fff3e0;
  font-weight: bold;
  font-size: 1.1em;
}

.report-table .detail-row td {
  background-color: #fafafa;
  color: #666;
}

.pl-4 {
  padding-left: 32px !important;
}
</style>
